import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Clock,
  Gamepad2,
  Gift,
  Hash,
  Loader2,
  MessageSquare,
  Monitor,
  MousePointerClick,
  Newspaper,
  Puzzle,
  ShoppingBag,
  Trophy,
  User,
  UserCheck,
  X,
} from 'lucide-react';
import api from '../api/client';

// Feature Usage Heatmap — how often customers use NoLag kiosk features, by café,
// over the last 7 days. Every count is backed by feature_events rows; hovering
// (or focusing) a count opens a card listing the actions behind it: who, what,
// when, which PC and session. Clicking pins the card so it can be scrolled.
//   GET /internal/feature-heatmap                       café × feature counts
//   GET /internal/feature-heatmap/events?cafe_id&feature  one cell's actions
//
// The News column is the one column that isn't a customer action — it counts
// announcements a café published — so it's rendered past a divider and its
// card drops the customer/PC/session facts that can't apply. The backend
// marks it with customer_action: false.

const DAYS = 7;
const POLL_MS = 60000;
const EVENTS_LIMIT = 25;
const NEWS_KEY = 'news_published';

const FEATURE_META = {
  chat_message: { icon: MessageSquare, one: 'message', many: 'messages', fallback: 'Chat message sent' },
  prize_redeem: { icon: Gift, one: 'redemption', many: 'redemptions', fallback: 'Prize redeemed' },
  challenge_claim: { icon: Trophy, one: 'claim', many: 'claims', fallback: 'Challenge claimed' },
  game_launch: { icon: Gamepad2, one: 'launch', many: 'launches', fallback: 'Game launched' },
  app_launch: { icon: Puzzle, one: 'launch', many: 'launches', fallback: 'App launched' },
  shop_purchase: { icon: ShoppingBag, one: 'purchase', many: 'purchases', fallback: 'Purchase' },
  news_published: { icon: Newspaper, one: 'announcement', many: 'announcements', fallback: 'Announcement' },
};
const DEFAULT_META = { icon: MousePointerClick, one: 'action', many: 'actions', fallback: 'Action' };

// Documented sequential blue, dark steps (600 → 150), validated as an ordered
// ramp against the card surface. Ink flips to near-black where white drops
// under 4.5:1.
const RAMP = [
  { bg: '#184f95', ink: '#ffffff' },
  { bg: '#256abf', ink: '#ffffff' },
  { bg: '#3987e5', ink: '#0b0b0b' },
  { bg: '#6da7ec', ink: '#0b0b0b' },
  { bg: '#b7d3f6', ink: '#0b0b0b' },
];

const metaFor = (key) => FEATURE_META[key] || DEFAULT_META;
const nounFor = (key, n) => (n === 1 ? metaFor(key).one : metaFor(key).many);

// Up to five value bins over 1..max; small maxima collapse to fewer bins.
function buildBins(max) {
  if (max <= 0) return [];
  const uppers = [];
  for (let k = 1; k <= 5; k += 1) {
    const u = Math.ceil((max * k) / 5);
    if (!uppers.includes(u)) uppers.push(u);
  }
  const n = uppers.length;
  return uppers.map((upper, i) => {
    const lower = i === 0 ? 1 : uppers[i - 1] + 1;
    const step = n === 1 ? 2 : Math.round((i * (RAMP.length - 1)) / (n - 1));
    return { lower, upper, label: lower === upper ? `${lower}` : `${lower}–${upper}`, ...RAMP[step] };
  });
}

const binFor = (bins, count) => bins.find((b) => count <= b.upper) || bins[bins.length - 1];

function formatDateTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatAgo(iso, now) {
  if (!iso) return '';
  const mins = Math.round((now - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function FeatureIcon({ featureKey, size = 15 }) {
  const Icon = metaFor(featureKey).icon;
  return <Icon size={size} aria-hidden="true" />;
}

function NewsRow({ ev, now }) {
  const by = ev.user_name || ev.user_email || null;
  return (
    <li className="fuh-ev">
      <div className="fuh-ev__top">
        <span className="fuh-ev__what">{ev.detail || 'Announcement'}</span>
        <span className="fuh-ev__ago" title={formatDateTime(ev.timestamp) || undefined}>
          {formatAgo(ev.timestamp, now)}
        </span>
      </div>
      <div className="fuh-ev__facts">
        <span className="fuh-ev__fact" title={ev.user_email || undefined}>
          <UserCheck size={13} aria-hidden="true" />
          {by ? `Posted by ${by}` : <span className="fuh-ev__none">Publisher not recorded</span>}
        </span>
        <span className="fuh-ev__fact">
          <Clock size={13} aria-hidden="true" />
          {formatDateTime(ev.timestamp) || '—'}
        </span>
      </div>
    </li>
  );
}

function EventRow({ ev, featureKey, now }) {
  if (featureKey === NEWS_KEY) return <NewsRow ev={ev} now={now} />;
  const meta = metaFor(featureKey);
  const who = ev.user_name || ev.user_email || (ev.user_id ? `User #${ev.user_id}` : 'Unknown customer');
  const pc = ev.pc_id ? `${ev.pc_name || 'PC'} · #${ev.pc_id}` : null;
  return (
    <li className="fuh-ev">
      <div className="fuh-ev__top">
        <span className={`fuh-ev__what ${ev.detail ? '' : 'fuh-ev__what--muted'}`}>
          {ev.detail || meta.fallback}
        </span>
        <span className="fuh-ev__ago" title={formatDateTime(ev.timestamp) || undefined}>
          {formatAgo(ev.timestamp, now)}
        </span>
      </div>
      <div className="fuh-ev__facts">
        <span className="fuh-ev__fact" title={ev.user_email || undefined}>
          <User size={13} aria-hidden="true" />
          {who}
        </span>
        <span className="fuh-ev__fact">
          <Monitor size={13} aria-hidden="true" />
          {pc || <span className="fuh-ev__none">No PC recorded</span>}
        </span>
        <span className="fuh-ev__fact">
          <Hash size={13} aria-hidden="true" />
          {ev.session_id ? `Session ${ev.session_id}` : <span className="fuh-ev__none">No session</span>}
        </span>
        <span className="fuh-ev__fact">
          <Clock size={13} aria-hidden="true" />
          {formatDateTime(ev.timestamp) || '—'}
        </span>
      </div>
    </li>
  );
}

export default function FeatureUsageHeatmap() {
  const [heat, setHeat] = useState(null); // { features, cafes }
  const [loadError, setLoadError] = useState(false);
  const [version, setVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [active, setActive] = useState(null); // { cafeId, cafeName, featureKey, label, count }
  const [pinned, setPinned] = useState(false);
  const [cells, setCells] = useState({}); // "cafe:feature" -> { data?, error?, v }
  const [now, setNow] = useState(() => Date.now());

  const anchorRef = useRef(null);
  const cardRef = useRef(null);
  const timers = useRef({ open: null, close: null });
  const inflight = useRef(new Set());

  // ---- heatmap data (60s poll) ----
  const load = useCallback(async () => {
    try {
      const res = await api.get('/internal/feature-heatmap', { params: { days: DAYS } });
      setHeat(res.data || { features: [], cafes: [] });
      setLoadError(false);
      setVersion((v) => v + 1);
      const t = Date.now();
      setUpdatedAt(t);
      setNow(t);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const run = () => {
      if (alive) load();
    };
    run();
    const id = setInterval(run, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [load]);

  // ---- per-cell events for the open card (stale data stays while refreshing) ----
  const activeKey = active ? `${active.cafeId}:${active.featureKey}` : null;

  useEffect(() => {
    if (!active) return;
    const key = `${active.cafeId}:${active.featureKey}`;
    const entry = cells[key];
    const flightKey = `${key}@${version}`;
    if ((entry && entry.v === version) || inflight.current.has(flightKey)) return;
    inflight.current.add(flightKey);
    api
      .get('/internal/feature-heatmap/events', {
        params: { cafe_id: active.cafeId, feature: active.featureKey, days: DAYS, limit: EVENTS_LIMIT },
      })
      .then((res) => setCells((prev) => ({ ...prev, [key]: { data: res.data, v: version } })))
      .catch(() => setCells((prev) => ({ ...prev, [key]: { ...prev[key], error: true, v: version } })))
      .finally(() => {
        inflight.current.delete(flightKey);
        setNow(Date.now());
      });
  }, [active, version, cells]);

  // ---- card positioning (imperative: measured after render, follows scroll) ----
  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const card = cardRef.current;
    if (!anchor || !card) return;
    const r = anchor.getBoundingClientRect();
    const w = card.offsetWidth;
    const h = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 10;
    let left = r.right + gap;
    if (left + w > vw - 12) left = r.left - w - gap;
    if (left < 12) left = Math.max(12, Math.min(vw - w - 12, r.left + r.width / 2 - w / 2));
    const top = Math.max(12, Math.min(vh - h - 12, r.top + r.height / 2 - h / 2));
    card.style.left = `${Math.round(left)}px`;
    card.style.top = `${Math.round(top)}px`;
    card.style.visibility = 'visible';
  }, []);

  useLayoutEffect(() => {
    if (active) place();
  });

  useEffect(() => {
    if (!active) return undefined;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [active, place]);

  // ---- open / close ----
  const clearTimers = () => {
    clearTimeout(timers.current.open);
    clearTimeout(timers.current.close);
  };

  const close = useCallback(() => {
    clearTimeout(timers.current.open);
    clearTimeout(timers.current.close);
    setActive(null);
    setPinned(false);
    anchorRef.current = null;
  }, []);

  const openFor = (el, cell) => {
    anchorRef.current = el;
    setActive(cell);
  };

  const onCellEnter = (e, cell) => {
    if (pinned) return;
    clearTimers();
    const el = e.currentTarget;
    timers.current.open = setTimeout(() => openFor(el, cell), 90);
  };

  const scheduleClose = () => {
    if (pinned) return;
    clearTimeout(timers.current.open);
    timers.current.close = setTimeout(close, 160);
  };

  const onCellFocus = (e, cell) => {
    if (pinned) return;
    clearTimers();
    openFor(e.currentTarget, cell);
  };

  const onCellClick = (e, cell) => {
    clearTimers();
    const same = activeKey === `${cell.cafeId}:${cell.featureKey}`;
    if (pinned && same) {
      close();
      return;
    }
    openFor(e.currentTarget, cell);
    setPinned(true);
  };

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        const anchor = anchorRef.current;
        close();
        anchor?.focus();
      }
    };
    const onDown = (e) => {
      if (!pinned) return;
      if (cardRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.fuh-cell--live')) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [active, pinned, close]);

  useEffect(() => () => clearTimers(), []);

  // ---- derived ----
  const features = useMemo(() => heat?.features ?? [], [heat]);
  const cafes = useMemo(() => heat?.cafes ?? [], [heat]);

  const summary = useMemo(() => {
    const featureTotals = Object.fromEntries(
      features.map((f) => [f.key, cafes.reduce((s, c) => s + (c.counts[f.key] || 0), 0)]),
    );
    const cafeTotals = cafes.map((c) => ({
      ...c,
      total: features.reduce((s, f) => s + (c.counts[f.key] || 0), 0),
    }));
    const total = cafeTotals.reduce((s, c) => s + c.total, 0);
    const topFeature = features.reduce(
      (best, f) => (featureTotals[f.key] > (best ? featureTotals[best.key] : 0) ? f : best),
      null,
    );
    const busiest = cafeTotals.reduce((best, c) => (c.total > (best ? best.total : 0) ? c : best), null);
    const usingCafes = cafeTotals.filter((c) => c.total > 0).length;
    const max = Math.max(0, ...cafes.flatMap((c) => features.map((f) => c.counts[f.key] || 0)));
    return { featureTotals, cafeTotals, total, topFeature, busiest, usingCafes, max };
  }, [features, cafes]);

  const bins = useMemo(() => buildBins(summary.max), [summary.max]);
  const entry = activeKey ? cells[activeKey] : null;

  // ---- render ----
  const renderCard = () => {
    if (!active) return null;
    const detail = entry?.data;
    const total = detail ? detail.total : active.count;
    const events = detail?.events ?? [];
    return createPortal(
      <div
        ref={cardRef}
        id="fuh-card"
        className={`fuh-card ${pinned ? 'fuh-card--pinned' : ''}`}
        role={pinned ? 'dialog' : 'tooltip'}
        aria-label={`${active.label} at ${active.cafeName}`}
        style={{ visibility: 'hidden' }}
        onPointerEnter={() => clearTimeout(timers.current.close)}
        onPointerLeave={scheduleClose}
      >
        <div className="fuh-card__head">
          <div className="fuh-card__title">
            <span className="fuh-chip">
              <FeatureIcon featureKey={active.featureKey} size={14} />
            </span>
            <span className="fuh-card__feature">{active.label}</span>
            <span className="fuh-card__dot" aria-hidden="true">·</span>
            <span className="fuh-card__cafe">{active.cafeName}</span>
            {pinned && (
              <button type="button" className="fuh-card__close" onClick={close} aria-label="Close details">
                <X size={15} />
              </button>
            )}
          </div>
          <div className="fuh-card__value">
            <span className="fuh-card__num">{total}</span>
            <span className="fuh-card__unit">{nounFor(active.featureKey, total)}</span>
            <span className="fuh-card__period">in the last {DAYS} days</span>
          </div>
        </div>

        <div className="fuh-card__body">
          {active.featureKey === NEWS_KEY && (
            <div className="fuh-card__aside">
              Published by the café — so there's no customer, PC or session on these, unlike
              the other columns.
            </div>
          )}
          {!detail && !entry?.error && (
            <div className="fuh-card__state">
              <Loader2 size={15} className="fuh-spin" aria-hidden="true" />
              Loading who did what…
            </div>
          )}
          {!detail && entry?.error && (
            <div className="fuh-card__state fuh-card__state--error">Couldn't load the details for this cell.</div>
          )}
          {detail && events.length === 0 && (
            <div className="fuh-card__state">No individual actions recorded for this cell.</div>
          )}
          {events.length > 0 && (
            <ul className="fuh-card__list">
              {events.map((ev) => (
                <EventRow key={ev.id} ev={ev} featureKey={active.featureKey} now={now} />
              ))}
            </ul>
          )}
        </div>

        <div className="fuh-card__foot">
          <span>
            {detail && total > events.length
              ? `Latest ${events.length} of ${total}`
              : detail
                ? `All ${total} shown`
                : ''}
          </span>
          <span>{pinned ? 'Esc to close' : 'Click the cell to pin'}</span>
        </div>
      </div>,
      document.body,
    );
  };

  const header = (
    <div className="fuh__header">
      <div>
        <h3 className="fuh__title">Feature Usage Heatmap</h3>
        <p className="fuh__subtitle">How often customers use kiosk features, by café · last {DAYS} days</p>
      </div>
      {updatedAt && (
        <span className="fuh__updated">
          <span className="fuh__live" aria-hidden="true" />
          Updated {new Date(updatedAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}
        </span>
      )}
    </div>
  );

  if (!heat) {
    return (
      <div className="fuh card">
        {header}
        <div className="fuh__empty">
          {loadError ? (
            <>
              <p>Couldn't load feature usage.</p>
              <button type="button" className="btn btn--secondary btn--sm" onClick={load}>
                Try again
              </button>
            </>
          ) : (
            <p className="fuh__muted">
              <Loader2 size={15} className="fuh-spin" aria-hidden="true" /> Loading feature usage…
            </p>
          )}
        </div>
        <style>{STYLES}</style>
      </div>
    );
  }

  const { featureTotals, cafeTotals, total, topFeature, busiest, usingCafes } = summary;
  const cols = `minmax(150px, 1.25fr) repeat(${features.length}, minmax(88px, 1fr)) minmax(64px, 0.55fr)`;

  return (
    <div className="fuh card">
      {header}

      <div className="fuh__tiles">
        <div className="fuh-tile">
          <span className="fuh-tile__label">Total actions</span>
          <span className="fuh-tile__value">{total.toLocaleString('en-IN')}</span>
          <span className="fuh-tile__sub">across {cafes.length} {cafes.length === 1 ? 'café' : 'cafés'}</span>
        </div>
        <div className="fuh-tile">
          <span className="fuh-tile__label">Most used feature</span>
          <span className="fuh-tile__value fuh-tile__value--icon">
            {topFeature ? (
              <>
                <span className="fuh-chip">
                  <FeatureIcon featureKey={topFeature.key} size={15} />
                </span>
                {topFeature.label}
              </>
            ) : '—'}
          </span>
          <span className="fuh-tile__sub">
            {topFeature
              ? `${featureTotals[topFeature.key]} ${nounFor(topFeature.key, featureTotals[topFeature.key])}`
              : 'No usage yet'}
          </span>
        </div>
        <div className="fuh-tile">
          <span className="fuh-tile__label">Busiest café</span>
          <span className="fuh-tile__value fuh-tile__value--text">{busiest ? busiest.cafe_name : '—'}</span>
          <span className="fuh-tile__sub">{busiest ? `${busiest.total} actions` : 'No usage yet'}</span>
        </div>
        <div className="fuh-tile">
          <span className="fuh-tile__label">Cafés using features</span>
          <span className="fuh-tile__value">
            {usingCafes}
            <span className="fuh-tile__of"> of {cafes.length}</span>
          </span>
          <span className="fuh-tile__sub">
            {cafes.length ? `${Math.round((usingCafes / cafes.length) * 100)}% of the network` : '—'}
          </span>
        </div>
      </div>

      {total === 0 ? (
        <div className="fuh__empty">
          <p>No activity in the last {DAYS} days.</p>
          <p className="fuh__muted">
            Chat messages, prize redemptions, challenge claims, game and app launches and purchases appear
            here as customers use the kiosk, alongside the announcements each café posts.
          </p>
        </div>
      ) : (
        <div className="fuh__scroll">
          <div className="fuh__grid" style={{ gridTemplateColumns: cols }} role="table" aria-label="Feature usage by café">
            <div className="fuh__row" role="row">
              <div className="fuh__corner" role="columnheader">Café</div>
              {features.map((f) => (
                <div
                  key={f.key}
                  className={`fuh__colhead ${f.customer_action === false ? 'fuh__colhead--aside' : ''}`}
                  role="columnheader"
                >
                  <div className="fuh__colname">
                    <span className="fuh-chip">
                      <FeatureIcon featureKey={f.key} size={14} />
                    </span>
                    {f.label}
                  </div>
                  <div className="fuh__meter" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, f.adoption_percent)}%` }} />
                  </div>
                  <div className="fuh__adopt">
                    {Math.round(f.adoption_percent)}% of cafés
                    {f.customer_action === false ? ' posted' : ''}
                  </div>
                </div>
              ))}
              <div className="fuh__colhead fuh__colhead--total" role="columnheader">Total</div>
            </div>

            {cafeTotals.map((c) => (
              <div key={c.cafe_id} className="fuh__row" role="row">
                <div className="fuh__cafe" role="rowheader" title={c.cafe_name}>{c.cafe_name}</div>
                {features.map((f) => {
                  const count = c.counts[f.key] || 0;
                  const cellKey = `${c.cafe_id}:${f.key}`;
                  const aside = f.customer_action === false ? ' fuh-cell--aside' : '';
                  if (count === 0) {
                    return (
                      <div key={f.key} role="cell" className={`fuh-cell fuh-cell--zero${aside}`} aria-label={`${c.cafe_name}, ${f.label}: none`}>
                        –
                      </div>
                    );
                  }
                  const bin = binFor(bins, count);
                  const cell = { cafeId: c.cafe_id, cafeName: c.cafe_name, featureKey: f.key, label: f.label, count };
                  const isActive = activeKey === cellKey;
                  return (
                    <div key={f.key} role="cell" className={`fuh__cellwrap${aside}`}>
                      <button
                        type="button"
                        className={`fuh-cell fuh-cell--live ${isActive ? 'fuh-cell--active' : ''}`}
                        style={{ background: bin.bg, color: bin.ink }}
                        aria-label={`${c.cafe_name}, ${f.label}: ${count} ${nounFor(f.key, count)} in the last ${DAYS} days. Show details.`}
                        aria-expanded={isActive}
                        aria-controls={isActive ? 'fuh-card' : undefined}
                        onPointerEnter={(e) => onCellEnter(e, cell)}
                        onPointerLeave={scheduleClose}
                        onFocus={(e) => onCellFocus(e, cell)}
                        onBlur={scheduleClose}
                        onClick={(e) => onCellClick(e, cell)}
                      >
                        {count}
                      </button>
                    </div>
                  );
                })}
                <div className="fuh__total" role="cell">{c.total}</div>
              </div>
            ))}

            <div className="fuh__row fuh__row--foot" role="row">
              <div className="fuh__cafe fuh__cafe--foot" role="rowheader">All cafés</div>
              {features.map((f) => (
                <div key={f.key} className="fuh__total" role="cell">{featureTotals[f.key]}</div>
              ))}
              <div className="fuh__total fuh__total--grand" role="cell">{total}</div>
            </div>
          </div>
        </div>
      )}

      {total > 0 && (
        <div className="fuh__footer">
          <div className="fuh__legend">
            <span className="fuh__legend-title">Actions per café</span>
            <span className="fuh__legend-item">
              <span className="fuh__swatch fuh__swatch--zero" />0
            </span>
            {bins.map((b) => (
              <span key={b.label} className="fuh__legend-item">
                <span className="fuh__swatch" style={{ background: b.bg }} />
                {b.label}
              </span>
            ))}
          </div>
          <span className="fuh__hint">
            <MousePointerClick size={14} aria-hidden="true" />
            Hover a number to see who, what, when and on which PC · click to pin
          </span>
        </div>
      )}

      {renderCard()}
      <style>{STYLES}</style>
    </div>
  );
}

const STYLES = `
  .fuh {
    margin-top: var(--space-6);
    padding: var(--space-6);
  }

  .fuh__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-4);
    margin-bottom: var(--space-5);
  }

  .fuh__title {
    font-size: var(--text-lg);
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .fuh__subtitle {
    margin-top: 4px;
    font-size: var(--text-sm);
    color: var(--text-tertiary);
  }

  .fuh__updated {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border: 1px solid var(--glass-border);
    border-radius: 999px;
    font-size: var(--text-xs);
    color: var(--text-secondary);
    white-space: nowrap;
  }

  .fuh__live {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #3987e5;
    box-shadow: 0 0 0 3px rgba(57, 135, 229, 0.18);
  }

  .fuh__muted {
    color: var(--text-tertiary);
  }

  .fuh-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    flex: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    color: var(--text-secondary);
  }

  .fuh-spin {
    animation: fuh-spin 0.9s linear infinite;
    vertical-align: -2px;
  }

  @keyframes fuh-spin {
    to { transform: rotate(360deg); }
  }

  /* Summary tiles */
  .fuh__tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--space-3);
    margin-bottom: var(--space-6);
  }

  .fuh-tile {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    padding: 14px 16px;
    border: 1px solid var(--glass-border);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.025);
  }

  .fuh-tile__label {
    font-size: var(--text-xs);
    color: var(--text-tertiary);
  }

  .fuh-tile__value {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--text-primary);
    line-height: 1.25;
  }

  .fuh-tile__value--icon {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
  }

  .fuh-tile__value--text {
    font-size: 18px;
    overflow-wrap: anywhere;
  }

  .fuh-tile__of {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--text-tertiary);
  }

  .fuh-tile__sub {
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }

  /* Matrix */
  .fuh__scroll {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 420px;
    margin: 0 calc(var(--space-2) * -1);
    padding: 0 var(--space-2) var(--space-1);
  }

  .fuh__grid {
    display: grid;
    column-gap: 6px;
    row-gap: 6px;
    min-width: 640px;
  }

  .fuh__row {
    display: contents;
  }

  /* Sticky header row -- .fuh__scroll now caps the matrix's height (so a
     café list of any size takes a fixed amount of page space), so the
     header needs to stay pinned during that vertical scroll or the column
     names scroll out of view along with the rows. */
  .fuh__corner,
  .fuh__colhead,
  .fuh__colhead--total {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--bg-elevated, #12121a);
  }

  .fuh__corner {
    align-self: end;
    padding-bottom: 10px;
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--text-tertiary);
  }

  .fuh__colhead {
    display: flex;
    flex-direction: column;
    gap: 7px;
    padding: 0 2px 10px;
    min-width: 0;
  }

  .fuh__colhead--total {
    align-self: end;
    align-items: flex-end;
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--text-tertiary);
  }

  .fuh__colname {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
  }

  .fuh__meter {
    height: 4px;
    border-radius: 2px;
    background: #0d366b;
    overflow: hidden;
  }

  .fuh__meter span {
    display: block;
    height: 100%;
    border-radius: 2px;
    background: #3987e5;
  }

  .fuh__adopt {
    font-size: 11px;
    color: var(--text-tertiary);
  }

  .fuh__cafe {
    display: flex;
    align-items: center;
    min-width: 0;
    padding-right: var(--space-3);
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--text-primary);
    overflow-wrap: anywhere;
  }

  .fuh__cafe--foot {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--text-tertiary);
  }

  .fuh__cellwrap {
    display: flex;
  }

  /* News: a café's own action, kept visually apart from the customer columns. */
  .fuh__colhead--aside,
  .fuh__cellwrap--aside,
  .fuh-cell--aside {
    border-left: 1px dashed var(--glass-border-hover, rgba(255, 255, 255, 0.14));
    padding-left: 10px;
    margin-left: 4px;
  }

  .fuh-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 46px;
    border: 0;
    border-radius: 10px;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
  }

  .fuh-cell--zero {
    background: rgba(255, 255, 255, 0.035);
    color: var(--text-tertiary);
    font-weight: 400;
  }

  .fuh-cell--live {
    cursor: pointer;
    transition: transform 120ms ease-out, box-shadow 120ms ease-out, filter 120ms ease-out;
  }

  .fuh-cell--live:hover,
  .fuh-cell--live:focus-visible,
  .fuh-cell--active {
    outline: none;
    filter: brightness(1.08);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
    transform: translateY(-1px);
  }

  .fuh__total {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 4px;
    font-size: var(--text-sm);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--text-secondary);
  }

  .fuh__row--foot > * {
    margin-top: 4px;
    padding-top: 10px;
    border-top: 1px solid var(--glass-border);
    min-height: 34px;
  }

  .fuh__row--foot .fuh__total {
    justify-content: center;
    color: var(--text-tertiary);
  }

  .fuh__row--foot .fuh__total--grand {
    justify-content: flex-end;
    color: var(--text-primary);
  }

  /* Legend + hint */
  .fuh__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3) var(--space-6);
    margin-top: var(--space-5);
  }

  .fuh__legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 14px;
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }

  .fuh__legend-title {
    color: var(--text-tertiary);
  }

  .fuh__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .fuh__swatch {
    width: 14px;
    height: 14px;
    border-radius: 4px;
  }

  .fuh__swatch--zero {
    background: rgba(255, 255, 255, 0.07);
  }

  .fuh__hint {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--text-xs);
    color: var(--text-tertiary);
  }

  .fuh__empty {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
    padding: var(--space-5);
    border: 1px dashed var(--glass-border);
    border-radius: 12px;
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }

  /* Hover card (portaled to <body>) */
  .fuh-card {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    width: 360px;
    max-width: calc(100vw - 24px);
    max-height: min(480px, calc(100vh - 24px));
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 14px;
    background: rgba(20, 20, 28, 0.97);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.35);
    color: var(--text-primary);
    font-family: var(--font-sans);
  }

  .fuh-card--pinned {
    border-color: rgba(57, 135, 229, 0.55);
  }

  .fuh-card__head {
    padding: 14px 16px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .fuh-card__title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 13px;
  }

  .fuh-card__feature {
    font-weight: 600;
  }

  .fuh-card__dot {
    color: var(--text-tertiary);
  }

  .fuh-card__cafe {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-secondary);
  }

  .fuh-card__close {
    display: inline-flex;
    margin-left: auto;
    padding: 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
  }

  .fuh-card__close:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--text-primary);
  }

  .fuh-card__value {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }

  .fuh-card__num {
    font-size: 28px;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1;
  }

  .fuh-card__unit {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .fuh-card__period {
    font-size: 12px;
    color: var(--text-tertiary);
  }

  .fuh-card__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 6px;
  }

  .fuh-card__state {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 10px;
    font-size: 13px;
    color: var(--text-secondary);
  }

  .fuh-card__state--error {
    color: var(--status-danger);
  }

  .fuh-card__aside {
    margin: 5px;
    padding: 9px 10px;
    border: 1px solid rgba(245, 166, 35, 0.3);
    border-radius: 9px;
    background: rgba(245, 166, 35, 0.12);
    font-size: 11.5px;
    line-height: 1.5;
    color: #f3c06a;
  }

  .fuh-card__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .fuh-ev {
    padding: 10px;
    border-radius: 10px;
  }

  .fuh-ev + .fuh-ev {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  .fuh-ev:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  .fuh-ev__top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  .fuh-ev__what {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    overflow-wrap: anywhere;
  }

  .fuh-ev__what--muted {
    font-weight: 500;
    color: var(--text-secondary);
  }

  .fuh-ev__ago {
    flex: none;
    font-size: 11px;
    color: var(--text-tertiary);
  }

  .fuh-ev__facts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 5px 12px;
    margin-top: 8px;
  }

  .fuh-ev__fact {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-size: 12px;
    color: var(--text-secondary);
    overflow-wrap: anywhere;
  }

  .fuh-ev__fact svg {
    flex: none;
    color: var(--text-tertiary);
  }

  .fuh-ev__none {
    color: var(--text-tertiary);
  }

  .fuh-card__foot {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 11px;
    color: var(--text-tertiary);
  }
`;
